import { Getter, Setter, subscribe } from '@dhmk/zustand-lens';
import { createContext, useContext, useEffect, useState } from 'react';

import { createStore, Mutate, StateCreator, StoreApi, StoreMutatorIdentifier, useStore } from 'zustand';

export type StateCreatorWithImmer<T> = StateCreator<T, [], [['zustand/immer', never]]>;

const StoreStateContext = createContext<unknown>(null);

type StoreStateProviderProps<T, Mos extends [StoreMutatorIdentifier, unknown][]> = { children: React.ReactNode } & (
  | { initializeStore: StateCreator<T, [], Mos>; storeFactory?: never }
  | { initializeStore?: never; storeFactory: () => Mutate<StoreApi<T>, Mos> }
);

function isStoreApi<T>(value: unknown): value is StoreApi<T> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'getState' in value &&
    typeof value.getState === 'function' &&
    'getInitialState' in value &&
    typeof value.getInitialState === 'function' &&
    'setState' in value &&
    typeof value.setState === 'function' &&
    'subscribe' in value &&
    typeof value.subscribe === 'function'
  );
}

export function StoreStateProvider<T, Mos extends [StoreMutatorIdentifier, unknown][] = []>({
  children,
  initializeStore,
  storeFactory,
}: StoreStateProviderProps<T, Mos>) {
  const [store] = useState<Mutate<StoreApi<T>, Mos>>(() =>
    storeFactory ? storeFactory() : createStore(initializeStore)
  );

  return <StoreStateContext.Provider value={store}>{children}</StoreStateContext.Provider>;
}

export function useStoreState<T, Selected>(selector: (state: T) => Selected): Selected {
  const store = useContext(StoreStateContext);

  if (!isStoreApi<T>(store)) {
    throw new Error('Missing StoreStateProvider');
  }

  return useStore(store, selector);
}

export function useStoreSubscription<T, Selected>(
  selector: (state: T) => Selected,
  effect: (state: Selected, prevState: Selected) => void,
  options: { equalityFn?: (state: Selected, newState: Selected) => boolean; fireImmediately?: boolean }
): void {
  const store = useContext(StoreStateContext);

  if (!isStoreApi<T>(store)) {
    throw new Error('Missing StoreStateProvider');
  }

  useEffect(() => subscribe(store, selector, effect, options), [store, selector, effect, options]);
}

export type StoreStateLens<T, InitialState = undefined> = InitialState extends undefined
  ? (set: Setter<T>, get: Getter<T>) => T
  : (set: Setter<T>, get: Getter<T>, initialState: InitialState) => T;
